<template>
  <BContainer
    v-if="currentUser"
    class="mb-5"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      v-if="fetching"
      class="gap-y-5 mt-5 d-flex justify-content-center align-items-center"
    >
      <BCol
        cols="12"
      >
        <div class="text-center py-5">
          <BIcon
            icon="stopwatch"
            font-scale="5"
            animation="cylon"
          />
          <p class="h3 py-2">
            <strong>Loading . . .</strong>
          </p>
        </div>
      </BCol>
    </BRow>
    <BRow
      v-if="!fetching && tradingTerms.length === 0"
      class="gap-y-5 mt-5 d-flex justify-content-center align-items-center"
    >
      <BCol
        cols="12"
        class="text-center py-5"
      >
        <BIcon
          icon="file-pdf"
          font-scale="5"
        />
        <p class="h3 py-2">
          <strong>No trading terms found</strong>
        </p>
      </BCol>
    </BRow>
    <BRow
      v-else
      class="gap-y-5 mt-5"
    >
      <BCol
        v-for="(term, index) in tradingTerms"
        :key="index"
        cols="12"
        md="6"
      >
        <div class="card p-3 rounded-lg">
          <div class="d-flex gap-2 align-items-center p-1 rounded-lg">
            <div class="d-flex align-items-center justify-content-center">
              <img
                :src="pdfImageSrc"
                class="img-fluid"
                alt="PDF Document"
                style="max-height: 100px;"
              >
            </div>
            <div class="d-flex flex-column gap-2 flex-grow-1">
              <div
                class="font-weight-bolder border-bottom pb-1"
                style="font-size: 15px;"
              >
                {{ term.term_name }}
              </div>
              <div class="d-flex gap-2 flex-column mt-2 small">
                <div>
                  Attachments: <b>{{ term.attachments?.length || 0 }}</b> File {{ term.attachments?.length === 1 ? '' : 's' }}
                </div>
                <div>
                  Created At: <b>{{ dateTimeShortFormatter(term?.created_at) || '' }}</b>
                </div>
                <div>
                  Updated At: <b>{{ dateTimeShortFormatter(term?.updated_at) || '' }}</b>
                </div>
              </div>
              <div class="d-flex justify-content-end">
                <BButton
                  variant="warning"
                  size="sm"
                  @click.prevent="() => viewTradingTerm(term)"
                >
                  View
                </BButton>
              </div>
            </div>
          </div>
        </div>
      </BCol>
    </BRow>

    <BModal
      ref="modalTradingTerm"
      size="sm"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
      title="Trading Term"
      @shown="() => attachArrowScroller('.nav-terms-tabs')"
      @hidden="() => detachArrowScroller('.nav-terms-tabs')"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-primary"
        nav-class="scrollable-x-tabs no-scrollbar nav-terms-tabs gap-x-1"
        content-class="scrollable"
        pills
      >
        <template #tabs-start>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-left bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <template #tabs-end>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="right: 20px; padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-right bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>

        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-file-lines" />
              <b class="is-tab-active ml-1">
                Details
              </b>
            </div>
          </template>

          <BRow>
            <BCol
              cols="12"
            >
              <ValidationObserver
                ref="formTradingTerm"
              >
                <form
                  role="form"
                  novalidate
                  @submit.prevent
                >
                  <BRow>

                    <BCol
                      cols="12"
                    >
                      <BFormGroup
                        label="Term Code"
                        label-for="fTradingTermCode"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <BFormInput
                          id="fTradingTermCode"
                          v-model="formTradingTerm.term_code"
                          type="text"
                          maxlength="150"
                          autocomplete="off"
                          disabled
                        />
                      </BFormGroup>
                    </BCol>

                    <BCol
                      cols="12"
                    >
                      <BFormGroup
                        label="Term Name"
                        label-for="fTradingTermName"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <BFormInput
                          id="fTradingTermName"
                          v-model="formTradingTerm.term_name"
                          type="text"
                          maxlength="150"
                          autocomplete="off"
                          disabled
                        />
                      </BFormGroup>
                    </BCol>

                    <BCol
                      cols="12"
                      md="6"
                    >
                      <BFormGroup
                        label="Start Date"
                        label-for="fStartDate"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <BFormInput
                          id="fStartDate"
                          v-model="formTradingTerm.start_date"
                          type="date"
                          disabled
                        />
                      </BFormGroup>
                    </BCol>

                    <BCol
                      cols="12"
                      md="6"
                    >
                      <BFormGroup
                        label="End Date"
                        label-for="fEndDate"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <BFormInput
                          id="fEndDate"
                          v-model="formTradingTerm.end_date"
                          type="date"
                          disabled
                        />
                      </BFormGroup>
                    </BCol>

                  </BRow>
                </form>
              </ValidationObserver>

            </BCol>
          </BRow>
        </BTab>

        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-paperclip" />
              <b class="is-tab-active ml-1">
                Attachments
              </b>
            </div>
          </template>
          <Attachment
            ref="attachmentUploader"
            :path="formTradingTerm.attachment_path"
            :files="formTradingTerm.attachments"
            :show-uploader="false"
          />
        </BTab>
      </BTabs>

      <template #modal-footer="{cancel}">
        <BButton
          variant="outline-dark"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Close
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>
<script>
import { mapGetters } from 'vuex'

import Attachment from '@/views/shared/documents/customer-memos/Attachment.vue'

import MxImageUrl from '@/mixins/image-url'
import MxFormatter from '@/mixins/formatter'
import MxMisc from '@/mixins/misc'

import { SSharedList } from '@/services/shared'

// const sampleData = [
//   {
//     title: 'Customer Consent Form',
//     src: 'consent-form.pdf'
//   }
// ]

export default {
  name: 'SharedDocumentDownloadables',

  components: {
    Attachment
  },

  mixins: [
    MxMisc,
    MxImageUrl,
    MxFormatter
  ],

  middleware: ['auth'],

  data () {
    return {
      fetching: false,
      formTradingTerm: {
        id: '',
        team_id: '',
        term_code: '',
        term_name: '',
        start_date: '',
        end_date: '',
        attachment_path: '',
        attachments: [],
        active: 1
      },
      tradingTerms: []
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    pdfImageSrc () {
      return `${window.location.origin}/img/pdf.png`
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getTradingTerms()
    }
  },

  methods: {
    getFormSrc (formName) {
      return `${window.location.origin}/forms/${formName}`
    },

    viewTradingTerm (term) {
      this.formTradingTerm = Object.assign(this.formTradingTerm, {
        id: term.id,
        team_id: term.team_id,
        term_code: term.term_code,
        term_name: term.term_name,
        start_date: term.start_date,
        end_date: term.end_date,
        attachment_path: term.attachment_path,
        attachments: term.attachments || [],
        active: 1
      })

      this.$refs.modalTradingTerm.show()
    },

    async getTradingTerms () {
      this.fetching = true

      return SSharedList.getTradingTerms().then(({ data }) => {
        this.tradingTerms = data.map(
          term => Object.assign(term, {
            src: 'consent-form.pdf'
          })
        )
      }).catch(() => {
        this.tradingTerms = []
      }).finally(() => {
        this.fetching = false
      })
    }
  }
}
</script>
<style scoped>
.form-content {
  max-height: 220px;
  min-height: 220px;
}

@media screen and (max-width: 985px) {
  .form-content {
    max-height: 150px;
    min-height: 150px;
  }
}

@media screen and (max-width: 516px) {
  .form-content {
    max-height: 180px;
    min-height: 180px;
  }
}

</style>
